import { useLocation, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Printer, ArrowLeft } from 'lucide-react';
import { DOCS, SUBPROCESSORS, COMPANY, LEGAL_VERSION, LEGAL_UPDATED } from '@/lib/legalContent';

const PATH_MAP = {
    '/terms': 'terms',
    '/privacy': 'privacy',
    '/dpa': 'dpa',
    '/sub-responsabili': 'subprocessors',
};

function Section({ s }) {
    return (
        <section className="mt-6">
            {s.h && <h2 className="text-base font-semibold text-[#0F172A] mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{s.h}</h2>}
            {(s.p || []).map((para, i) => (
                <p key={i} className="text-sm text-[#475569] leading-relaxed mb-2">{para}</p>
            ))}
            {s.list && (
                <ul className="list-disc pl-5 space-y-1">
                    {s.list.map((li, i) => <li key={i} className="text-sm text-[#475569] leading-relaxed">{li}</li>)}
                </ul>
            )}
        </section>
    );
}

function SubprocessorsTable() {
    return (
        <div className="mt-6 overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm" data-testid="subprocessors-table">
                <thead>
                    <tr className="bg-[#F2F5F2]">
                        <th className="p-2 border border-[#E2E8F0] font-semibold text-[#0F172A]">Fornitore</th>
                        <th className="p-2 border border-[#E2E8F0] font-semibold text-[#0F172A]">Ruolo / Finalità</th>
                        <th className="p-2 border border-[#E2E8F0] font-semibold text-[#0F172A]">Dati trattati</th>
                        <th className="p-2 border border-[#E2E8F0] font-semibold text-[#0F172A]">Ubicazione / Trasferimenti</th>
                    </tr>
                </thead>
                <tbody>
                    {SUBPROCESSORS.map((sp, i) => (
                        <tr key={i} className="align-top" data-testid={`subprocessor-row-${i}`}>
                            <td className="p-2 border border-[#E2E8F0] font-medium text-[#0F172A]">{sp.name}</td>
                            <td className="p-2 border border-[#E2E8F0] text-[#475569]">{sp.role}</td>
                            <td className="p-2 border border-[#E2E8F0] text-[#475569]">{sp.data}</td>
                            <td className="p-2 border border-[#E2E8F0] text-[#475569]">{sp.location}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

export default function LegalPage() {
    const location = useLocation();
    const key = PATH_MAP[location.pathname] || 'terms';
    const doc = DOCS[key];
    const isDpa = key === 'dpa';

    return (
        <div className="min-h-screen bg-[#F2F5F2] py-8 px-4 print:bg-white print:py-0">
            <div className="max-w-3xl mx-auto bg-white border border-[#CBD5E1] rounded-xl p-6 sm:p-8 print:border-0 print:shadow-none" data-testid={`legal-page-${key}`}>
                <div className="flex items-start justify-between gap-4 print:hidden">
                    <Button asChild variant="ghost" size="sm" className="text-[#64748B] -ml-2">
                        <Link to="/signup"><ArrowLeft className="h-4 w-4 mr-1" /> Indietro</Link>
                    </Button>
                    {isDpa && (
                        <Button variant="outline" size="sm" onClick={() => window.print()} className="border-[#1B4E8E] text-[#1B4E8E]" data-testid="dpa-print-btn">
                            <Printer className="h-4 w-4 mr-1" /> Stampa / Salva PDF
                        </Button>
                    )}
                </div>

                <h1 className="text-2xl font-bold text-[#0F172A] mt-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>{doc.title}</h1>
                {doc.subtitle && <p className="text-sm text-[#64748B] mt-1">{doc.subtitle}</p>}
                <p className="text-xs text-[#94A3B8] mt-1">Versione {LEGAL_VERSION} — ultimo aggiornamento: {LEGAL_UPDATED}</p>

                {doc.intro && <p className="text-sm text-[#475569] leading-relaxed mt-5">{doc.intro}</p>}

                {doc.table && <SubprocessorsTable />}

                {(doc.sections || []).map((s, i) => <Section key={i} s={s} />)}

                {doc.signature && (
                    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-8" data-testid="dpa-signature-block">
                        <div>
                            <p className="text-sm font-semibold text-[#0F172A]">Il Titolare del trattamento (Cliente)</p>
                            <p className="text-xs text-[#64748B] mt-1">Ragione sociale: _______________________</p>
                            <p className="text-xs text-[#64748B] mt-1">Luogo e data: _______________________</p>
                            <p className="text-xs text-[#64748B] mt-6">Firma: _______________________</p>
                        </div>
                        <div>
                            <p className="text-sm font-semibold text-[#0F172A]">Il Responsabile del trattamento</p>
                            <p className="text-xs text-[#64748B] mt-1">{COMPANY.name}</p>
                            <p className="text-xs text-[#64748B] mt-1">P.IVA {COMPANY.vat}</p>
                            <p className="text-xs text-[#64748B] mt-1">{COMPANY.address}</p>
                            <p className="text-xs text-[#64748B] mt-6">Firma: _______________________</p>
                        </div>
                    </div>
                )}

                <div className="mt-10 pt-6 border-t border-[#E2E8F0] print:hidden">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#64748B]">
                        <Link to="/terms" className="hover:text-[#1B4E8E] hover:underline">Termini di Servizio</Link>
                        <Link to="/privacy" className="hover:text-[#1B4E8E] hover:underline">Privacy Policy</Link>
                        <Link to="/dpa" className="hover:text-[#1B4E8E] hover:underline">DPA (art. 28)</Link>
                        <Link to="/sub-responsabili" className="hover:text-[#1B4E8E] hover:underline">Sub-responsabili</Link>
                    </div>
                    <p className="text-[10px] text-[#94A3B8] mt-3">Documento fornito come bozza informativa. Si consiglia la validazione da parte del proprio consulente legale/DPO prima dell’uso definitivo.</p>
                </div>
            </div>
        </div>
    );
}
